import { Button } from '@missionlabs/smartagent-app-components'
import { Spinner } from 'amazon-chime-sdk-component-library-react'
import { saveAs } from 'file-saver'
import useHasFeature, { AppFeatures, AppSubFeatures } from 'hooks/useHasFeature'
import JSZip from 'jszip'
import { useState } from 'react'
import { useDispatch } from 'react-redux'
import { deleteScreenshots } from 'store/videoMeeting/videoMeeting.reducer'
import {
    ScreenshotData,
    Screenshots as ScreenshotState,
} from 'store/videoMeeting/videoMeeting.state'
import { DownloadIcon } from './Icons/Download'
import { ScreenshotsHeading } from './ScreenshotsHeading'
import styles from './index.module.scss'

export interface ScreenshotsProps {
    isHistorical?: boolean
    screenshots?: ScreenshotState
    contactID: string
    attributeAttachmentCount: string
}

export const Screenshots = ({
    screenshots,
    contactID,
    isHistorical = false,
    attributeAttachmentCount,
}: ScreenshotsProps) => {
    const dispatch = useDispatch()

    const [fullscreen, setFullscreen] = useState<string | null>(null)
    const [selectedScreenshots, setSelectedScreenshots] = useState<ScreenshotState>({})
    const [downloading, setDownloading] = useState(false)

    const selectScreenshot = (screenshot: ScreenshotState) => {
        const keys = Object.keys(selectedScreenshots)
        const stateCopy = Object.assign({}, selectedScreenshots)
        if (keys.includes(Object.keys(screenshot)[0])) {
            delete stateCopy[Object.keys(screenshot)[0]]
            setSelectedScreenshots(stateCopy)
        } else {
            setSelectedScreenshots({ ...stateCopy, ...screenshot })
        }
    }

    const handleDelete = (screenshots: ScreenshotState, resetSelected?: boolean) => {
        dispatch(deleteScreenshots({ screenshots, isHistorical, attributeAttachmentCount }))
        if (resetSelected) setSelectedScreenshots({})
    }

    const handleBulkDownload = async () => {
        setDownloading(true)
        const toDownload = Object.keys(selectedScreenshots).length
            ? selectedScreenshots
            : screenshots!
        const zip = new JSZip()
        const folder = zip.folder(`${contactID}_screenshots`)

        await Promise.all(
            Object.entries(toDownload).map(async ([key, screenshot], idx) => {
                try {
                    const response = await fetch(screenshot.src)
                    const blob = await response.blob()
                    folder?.file(`${contactID}_screenshot_${idx + 1}.png`, blob)
                } catch (e) {
                    console.error(`Error downloading screenshot ${key}:`, e)
                }
            }),
        )

        await zip.generateAsync({ type: 'blob' }).then((content) => {
            saveAs(content, `${contactID}_screenshots.zip`)
        })
        setDownloading(false)
    }

    const handleDownload = (screenshot: ScreenshotData) => {
        const link = document.createElement('a')
        link.setAttribute('download', `${contactID}_video-session-screenshot.png`)
        link.target = '_blank'
        link.href = screenshot.src
        link.click()
    }

    const canUseFeature = useHasFeature()
    const canEditVideoSession = canUseFeature(AppFeatures.VIDEO_MEETING, [
        AppSubFeatures.VIDEO_MEETING_EDIT_RECORDS,
    ])

    const hasScreenshots = !!Object.keys(screenshots ?? {}).length
    const hasSelectedScreenshots = !!Object.keys(selectedScreenshots).length

    const getDownloadMessage = () => {
        return downloading
            ? 'Downloading'
            : `Download ${hasSelectedScreenshots ? 'Selected' : 'All'}`
    }

    if (screenshots) {
        return (
            <>
                {fullscreen && (
                    <div className={styles.fullscreenContainer}>
                        <div className={styles.fullscreen}>
                            <span></span>
                            <img
                                src={fullscreen}
                                className={styles.fullScreenImage}
                                alt="screenshot"
                            />
                            <Button
                                styling="white"
                                onClick={() => setFullscreen(null)}
                                className={styles.screenshotButton}
                            >
                                <span
                                    className={['xsm-mar-right', styles.closeIcon].join(' ')}
                                ></span>
                            </Button>
                        </div>
                    </div>
                )}
                {canEditVideoSession && (
                    <ScreenshotsHeading count={Object.keys(screenshots).length}>
                        {hasSelectedScreenshots && (
                            <Button
                                styling="red"
                                className={styles.button}
                                onClick={() => handleDelete(selectedScreenshots, true)}
                            >
                                Delete Selected
                            </Button>
                        )}
                        {hasScreenshots && (
                            <Button
                                styling="primary"
                                className={styles.button}
                                elastic
                                onClick={() => handleBulkDownload()}
                                disabled={downloading}
                            >
                                <span className={['xsm-mar-right'].join(' ')}>
                                    {downloading ? (
                                        <Spinner width="15px" />
                                    ) : (
                                        <DownloadIcon color="white" />
                                    )}
                                </span>
                                {getDownloadMessage()}
                            </Button>
                        )}
                    </ScreenshotsHeading>
                )}

                <div className={styles.screenshotContainer}>
                    {Object.entries(screenshots).map(([key, screenshot], i) => (
                        <div className={styles.screenshot} key={key}>
                            <div className={styles.ScreenshotContents}>
                                <div className={styles.img}>
                                    <img src={screenshot.src} alt="screenshot" />
                                </div>
                                <div className={styles.screenshotButtons}>
                                    <span>
                                        {canEditVideoSession && (
                                            <input
                                                type="checkbox"
                                                aria-label="Select Screenshot"
                                                className={styles.screenshotCheck}
                                                onClick={() =>
                                                    selectScreenshot({ [key]: screenshot })
                                                }
                                            />
                                        )}{' '}
                                    </span>
                                    <span>
                                        <Button
                                            styling="white"
                                            aria-label="Download Screenshot"
                                            className={styles.screenshotButton}
                                            onClick={() => handleDownload(screenshot)}
                                        >
                                            <span
                                                className={[
                                                    'xsm-mar-right',
                                                    styles.downloadIcon,
                                                ].join(' ')}
                                            ></span>
                                        </Button>
                                        <Button
                                            styling="white"
                                            aria-label="Fullscreen Screenshot"
                                            onClick={() => setFullscreen(screenshot.src)}
                                            className={styles.screenshotButton}
                                        >
                                            <span
                                                className={[
                                                    'xsm-mar-right',
                                                    styles.fullscreenIcon,
                                                ].join(' ')}
                                            ></span>
                                        </Button>
                                    </span>
                                    <span>
                                        {canEditVideoSession && (
                                            <Button
                                                styling="red"
                                                className={styles.screenshotButton}
                                                aria-label="Delete Screenshot"
                                                onClick={() => handleDelete({ [key]: screenshot })}
                                            >
                                                <span
                                                    className={[
                                                        'xsm-mar-right',
                                                        styles.deleteIcon,
                                                    ].join(' ')}
                                                ></span>
                                            </Button>
                                        )}
                                    </span>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </>
        )
    }
}
